import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
// 直接导入 webApi，确保在 Web 环境下 desktopAPI 已挂载
import '../services/webApi.js';

const AppDataContext = createContext(null);

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [appConfig, setAppConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // 加载配置
  const loadConfig = useCallback(async () => {
    try {
      // Web 环境下 webApi.js 已经挂载了 window.desktopAPI
      // 不需要等待，直接使用
      const api = window.desktopAPI;
      
      if (api && typeof api.getConfig === 'function') {
        try {
          const config = await api.getConfig();
          if (config) {
            setAppConfig(config);
            setGroups(config.groups || []);
            setItems(config.items || []);
            console.log('[AppDataContext] 配置加载成功');
          }
        } catch (apiErr) {
          console.warn('[AppDataContext] API 调用失败，使用默认配置:', apiErr.message);
          setGroups([]);
          setItems([]);
        }
      } else {
        console.warn('[AppDataContext] desktopAPI 不可用，使用默认配置');
        setGroups([]);
        setItems([]);
      }
    } catch (err) {
      console.error('[AppDataContext] 加载配置失败:', err);
      // 即使加载失败，也设置默认值，避免白屏
      setGroups([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 保存配置
  const saveConfig = useCallback(async (newConfig) => {
    try {
      if (window.desktopAPI) {
        const result = await window.desktopAPI.saveConfig(newConfig);
        if (result && result.success) {
          setAppConfig(newConfig);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('保存配置失败:', err);
      return false;
    }
  }, []);

  // 保存分组（自动保存，立即持久化）
  const saveGroups = useCallback(async (newGroups) => {
    try {
      if (window.desktopAPI) {
        // 先保存到后端
        const result = await window.desktopAPI.saveGroups(newGroups);
        if (result && result.success) {
          // 保存成功后再更新本地状态
          setGroups(newGroups);
          console.log('✅ 分组已自动保存');
          return true;
        } else {
          console.error('❌ 保存分组失败:', result?.message || '未知错误');
          return false;
        }
      }
      return false;
    } catch (err) {
      console.error('保存分组失败:', err);
      return false;
    }
  }, []);

  // 保存应用项（自动保存，立即持久化）
  const saveItems = useCallback(async (newItems) => {
    try {
      if (window.desktopAPI) {
        // 先保存到后端
        const result = await window.desktopAPI.saveItems(newItems);
        if (result && result.success) {
          // 保存成功后再更新本地状态
          setItems(newItems);
          console.log('✅ 应用项已自动保存');
          return true;
        } else {
          console.error('❌ 保存应用项失败:', result?.message || '未知错误');
          return false;
        }
      }
      return false;
    } catch (err) {
      console.error('保存应用项失败:', err);
      return false;
    }
  }, []);

  // 初始化加载
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const value = React.useMemo(() => ({
    groups,
    items,
    appConfig,
    loading,
    setGroups,
    setItems,
    setAppConfig,
    saveConfig,
    saveGroups,
    saveItems,
    reloadConfig: loadConfig,
  }), [
    groups,
    items,
    appConfig,
    loading,
    setGroups,
    setItems,
    setAppConfig,
    saveConfig,
    saveGroups,
    saveItems,
    loadConfig
  ]);

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

